import React from "react";
import { useTranslation } from "react-i18next";
import { ReportIndicators, ProjectReport } from "@planzone/dto";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectReportTemplate,
  useRetrieveSecondaryCriteriaTemplate,
  useSubmitProjectReport,
} from "@planzone/view-models";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { Props } from "./types";
import { IndicatorsForm } from "./indicatorsForm";
import { TextFieldsForm } from "./textFieldsForm";
import { TemplateSelector } from "./templateSelector";
import styles from "./styles.scss";

function ProjectReportSubmission({ project, initialReport, close }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    reportTemplates,
    retrieveProjectReportTemplate,
  } = useRetrieveProjectReportTemplate(planzone);
  const {
    isRequestPending: isSubmitProjectReportRequestPending,
    submitProjectReport,
  } = useSubmitProjectReport(project);
  const {
    template: criteriaTemplate,
    isRequestPending: isCriteriaRequestPending,
    isRequestSuccess: isCriteriaRequestSuccess,
    isRequestFailure: isCriteriaRequestFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "projectReport");
  const { account } = useAuthentication();
  const [name, setName] = React.useState(initialReport?.name || "");
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string>(
    initialReport?.templateId || "",
  );
  const [textFieldsForm, setTextFieldsForm] = React.useState<
    ProjectReport["fields"]
  >([]);
  const [indicatorsForm, setIndicatorsForm] = React.useState<
    ProjectReport["fields"]
  >([]);
  const [attributesForm, setAttributesForm] = React.useState<{
    [key: string]: string;
  }>(initialReport?.attributes || {});

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectReportTemplate(planzone);
  }, []);

  React.useEffect(() => {
    !isCriteriaRequestPending &&
      !isCriteriaRequestSuccess &&
      !isCriteriaRequestFailure &&
      retrieveSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "projectReport",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    if (reportTemplates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(reportTemplates[0].uuid);
    }
  }, [reportTemplates]);

  const selectedTemplate =
    reportTemplates.find((t) => t.uuid === selectedTemplateId) ||
    reportTemplates[0];

  React.useEffect(() => {
    if (!selectedTemplate) return;
    setTextFieldsForm(
      selectedTemplate.textFields.map((field) => {
        const existing = initialReport?.fields.find(
          (f) => f.name === field.name && f.type === "text",
        );
        return { ...field, value: existing?.value ?? "" };
      }),
    );
    setIndicatorsForm(
      selectedTemplate.indicators.map((field) => {
        const existing = initialReport?.fields.find(
          (f) => f.name === field.name && f.type === "indicator",
        );
        return {
          ...field,
          value: existing?.value ?? ReportIndicators.VERY_GOOD,
        };
      }),
    );
  }, [selectedTemplate?.uuid]);

  return (
    <Modal
      header={t("projectReportSubmission.title")}
      headerClassName={styles.header}
      close={close}
    >
      <form
        className={styles.projectReportSubmission}
        onSubmit={(event) => {
          event.preventDefault();
          !isSubmitProjectReportRequestPending &&
            submitProjectReport({
              project,
              report: {
                name:
                  name ||
                  t("projectReportSubmission.defaultTitle", {
                    date: new Date().toLocaleDateString(),
                  }),
                author: `${account.firstName} ${account.lastName}`,
                submissionDate: new Date().toISOString(),
                fields: [
                  ...indicatorsForm,
                  ...textFieldsForm.map((f) =>
                    f.criteriaId
                      ? { ...f, value: attributesForm[f.criteriaId] || "" }
                      : f,
                  ),
                ],
                attributes: attributesForm,
                templateId: selectedTemplateId,
              },
            });
        }}
      >
        <div className={styles.main}>
          <div className={styles.group}>
            <h3 className={styles.subTitle}>
              {t("projectReportSubmission.nameTitle")}
            </h3>
            <input
              className={styles.input}
              placeholder={t("projectReportSubmission.namePlaceholder")}
              value={name}
              onChange={({ target }) => setName(target.value)}
            />
          </div>
          {reportTemplates.length > 1 && (
            <div className={styles.group}>
              <h3 className={styles.subTitle}>
                {t("projectReportSubmission.templateSelect")}
              </h3>
              <TemplateSelector
                reportTemplates={reportTemplates}
                selectedTemplateId={selectedTemplateId}
                onSelect={setSelectedTemplateId}
              />
            </div>
          )}
          {indicatorsForm.length > 0 && (
            <div className={styles.group}>
              <IndicatorsForm
                indicators={indicatorsForm}
                editIndicators={setIndicatorsForm}
              />
            </div>
          )}
          <div className={styles.group}>
            <TextFieldsForm
              textFields={textFieldsForm}
              editTextFields={setTextFieldsForm}
              secondaryCriteria={criteriaTemplate}
              attributesForm={attributesForm}
              onAttributeChange={(criteriaId, value) =>
                setAttributesForm((prev) => ({
                  ...prev,
                  [criteriaId]: value,
                }))
              }
              disabled={isSubmitProjectReportRequestPending}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            type="submit"
            className={styles.button}
            status={isSubmitProjectReportRequestPending ? "pending" : "none"}
            isDisabled={isSubmitProjectReportRequestPending}
          >
            {t("projectReportSubmission.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { ProjectReportSubmission };
